import { Box } from '@mui/material';

import {
  selectLatestDatasetReleaseDisplayName,
  selectLatestDatasetReleaseId,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { DomainType } from '@verantos-dev/types-cohorts';

import { CohortCharacteristicsContent } from './components/CohortCharacteristicsContent';
import { CohortCharacteristicsDataDialog } from './components/CohortCharacteristicsDataDialog';
import { CohortCharacteristicsHeader } from './components/CohortCharacteristicsHeader';
import { CustomConceptGroupsDialog } from './components/CustomConceptGroupsDialog';
import { useCohortCharacteristics } from './hooks/useCohortCharacteristics';
import { useCohortCharacteristicsHeader } from './hooks/useCohortCharacteristicsHeader';
import { useCohortCharacteristicsWithCustomSelection } from './hooks/useCohortCharacteristicsWithCustomSelection';

export interface CohortCharacteristicsProps {
  projectId: string;
  cohortId: string;
}

export const CohortCharacteristics = ({ projectId, cohortId }: CohortCharacteristicsProps) => {
  const datasetReleaseId = useCohortDefinitionStore(selectLatestDatasetReleaseId);
  const datasetReleaseDisplayName = useCohortDefinitionStore(selectLatestDatasetReleaseDisplayName);
  const {
    theme,
    latestCohortStatus,
    isCohortSummaryInitiallyLoading,
    displayDataDialog,
    onHideDataDialog,
    onShowDataDialog,
    totalSubjectCount,
    cohortSummaryData,
    hasSummaryData,
    cohortMessages,
  } = useCohortCharacteristics({ projectId, cohortId });

  const {
    onCharacteristicsChange,
    selectedCharacteristicsType,
    ClinicalCharacteristicsDomainOptions,
    CharacteristicTypeOptions,
    selectedDomain,
    onDomainChange,
  } = useCohortCharacteristicsHeader();

  const {
    isCustomDialogOpen,
    handleOpenCustomDialog,
    handleCloseCustomDialog,
    isLoadingAvailableGroups,
    isLoadingCustomData,
    customDataError,
    hasCustomSelection,
    handleApplyCustomSelection,
    handleResetToDefault,
    chartTitle,
    getDisplayData,
    getAvailableConceptGroupsAsDropdownItems,
    getSelectedConceptGroupsAsDropdownItems,
  } = useCohortCharacteristicsWithCustomSelection({
    projectId,
    cohortId,
    selectedDomain: selectedDomain as DomainType,
    cohortSummaryData,
  });

  return (
    <Box
      gap={theme.spacing(4)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        // Relaxed from `md` so the characteristics column can shrink with
        // the Research Assistant sidebar open beside it; charts and the
        // donut row are now container-responsive down to this width.
        minWidth: theme.breakpoints.values.sm,
      }}
    >
      {hasSummaryData && (
        <CohortCharacteristicsHeader
          theme={theme}
          cohortMessages={cohortMessages}
          CharacteristicTypeOptions={CharacteristicTypeOptions}
          onCharacteristicsChange={onCharacteristicsChange}
          selectedCharacteristicsType={selectedCharacteristicsType}
          ClinicalCharacteristicsDomainOptions={ClinicalCharacteristicsDomainOptions}
          onDomainChange={onDomainChange}
          selectedDomain={selectedDomain}
          onShowDataDialog={onShowDataDialog}
          onCustomizeConceptGroups={handleOpenCustomDialog}
          onResetCustomSelection={handleResetToDefault}
          hasCustomSelection={hasCustomSelection}
          isLoadingCustomData={isLoadingCustomData}
        />
      )}

      <CohortCharacteristicsContent
        isCohortSummaryInitiallyLoading={isCohortSummaryInitiallyLoading}
        latestCohortStatus={latestCohortStatus}
        cohortSummaryData={cohortSummaryData}
        hasSummaryData={hasSummaryData}
        theme={theme}
        cohortMessages={cohortMessages}
        clinicalCharacteristicsDomainType={selectedDomain as DomainType}
        customChartTitle={chartTitle}
        customDisplayData={
          cohortSummaryData?.[selectedDomain as DomainType]
            ? getDisplayData(cohortSummaryData[selectedDomain as DomainType])
            : undefined
        }
        isLoadingCustomData={isLoadingCustomData}
      />

      {displayDataDialog && cohortSummaryData && (
        <CohortCharacteristicsDataDialog
          totalSubjectCount={totalSubjectCount}
          onHideDataDialog={onHideDataDialog}
          cohortSummaryData={cohortSummaryData}
          clinicalCharacteristicsDomainType={selectedDomain as DomainType}
          selectedCharacteristicType={selectedCharacteristicsType || ''}
          customDisplayData={
            selectedDomain && cohortSummaryData[selectedDomain as DomainType]
              ? getDisplayData(cohortSummaryData[selectedDomain as DomainType])
              : undefined
          }
          hasAnyCustomSelection={hasCustomSelection}
        />
      )}

      {isCustomDialogOpen &&
        selectedDomain &&
        getAvailableConceptGroupsAsDropdownItems &&
        getSelectedConceptGroupsAsDropdownItems &&
        handleApplyCustomSelection && (
          <CustomConceptGroupsDialog
            open={isCustomDialogOpen}
            onClose={handleCloseCustomDialog}
            domain={selectedDomain as DomainType}
            availableConceptGroups={getAvailableConceptGroupsAsDropdownItems()}
            selectedConceptGroups={getSelectedConceptGroupsAsDropdownItems()}
            onApplySelection={handleApplyCustomSelection}
            isLoading={isLoadingAvailableGroups || isLoadingCustomData}
            error={customDataError || undefined}
            datasetReleaseId={datasetReleaseId}
            datasetReleaseDisplayName={datasetReleaseDisplayName}
          />
        )}
    </Box>
  );
};
